import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, MapPin, Clock, Globe, Lock, Star, BadgeCheck, Flag } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { EnquiryDialog } from "@/components/discovery/EnquiryDialog";
import { FeedbackBlock } from "@/components/discovery/FeedbackBlock";

export default function VendorDetail() {
  const { slug } = useParams();
  const [v, setV] = useState(null);
  const [err, setErr] = useState(false);
  const [claim, setClaim] = useState({ name: "", mobile: "", email: "", message: "" });
  const [claimOpen, setClaimOpen] = useState(false);
  useEffect(() => { api.get(`/discovery/vendor/${slug}`).then((r) => setV(r.data)).catch(() => setErr(true)); }, [slug]);

  const submitClaim = async () => {
    if (!claim.name || !claim.mobile || !claim.email) return toast.error("Name, mobile and email are required");
    try { const r = await api.post(`/discovery/vendor/${slug}/claim`, claim); toast.success(r.data.message); setClaimOpen(false); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (err) return <div className="ca-container py-16 text-slate-500">Vendor not found.</div>;
  if (!v) return <div className="ca-container py-16 text-slate-500">Loading…</div>;

  return (
    <div className="ca-container py-10 max-w-3xl">
      <Link to={`/vendors/${v.category_slug}`} className="inline-flex items-center gap-1.5 text-sm text-teal mb-6"><ArrowLeft className="h-4 w-4" /> Back to category</Link>
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="font-display text-2xl font-bold text-slate-900">{v.name}</h1>
            <p className="text-sm text-slate-500 flex items-center gap-1 mt-1"><MapPin className="h-4 w-4" /> {v.city}{v.locality ? ` · ${v.locality}` : ""}</p>
          </div>
          <div className="flex flex-col items-end gap-1">
            {v.sponsored && <span className="rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-[10px] font-bold uppercase">Sponsored</span>}
            {v.featured && !v.sponsored && <span className="rounded-full bg-indigo-100 text-indigo-700 px-2 py-0.5 text-[10px] font-bold uppercase">Featured</span>}
          </div>
        </div>
        {v.is_demo && <span className="inline-block mt-2 rounded bg-slate-100 text-slate-500 px-2 py-0.5 text-[11px] font-semibold">{v.demo_label}</span>}
        <p className="text-slate-700 mt-3">{v.description}</p>

        {v.services?.length > 0 && (
          <div className="mt-4">
            <p className="text-xs font-semibold text-slate-500 mb-1">Services offered</p>
            <div className="flex flex-wrap gap-1.5">{v.services.map((s) => <span key={s} className="rounded-full bg-teal-brand/10 text-teal px-2.5 py-1 text-xs">{s}</span>)}</div>
          </div>
        )}

        <div className="mt-4 grid sm:grid-cols-3 gap-2 text-sm">
          <div className="rounded-lg bg-slate-50 p-3"><p className="text-xs text-slate-400 flex items-center gap-1"><Clock className="h-3 w-3" /> Hours</p><p className="font-medium text-slate-700">{v.business_hours}</p></div>
          <div className="rounded-lg bg-slate-50 p-3"><p className="text-xs text-slate-400 flex items-center gap-1"><BadgeCheck className="h-3 w-3" /> Status</p><p className="font-medium text-slate-700">{v.verification_status}</p></div>
          <div className="rounded-lg bg-slate-50 p-3"><p className="text-xs text-slate-400 flex items-center gap-1"><Star className="h-3 w-3" /> Feedback</p><p className="font-medium text-slate-700">{v.feedback_summary?.positive || 0}/{v.feedback_summary?.total || 0} positive</p></div>
        </div>

        <div className="mt-5 flex items-center gap-3">
          <EnquiryDialog categorySlug={v.category_slug} subcategorySlug={v.subcategory_slug} vendorSlug={v.slug}
            trigger={<Button className="bg-teal-brand hover:bg-teal-700 text-white" data-testid="vendor-detail-enquiry">Contact through platform</Button>} />
          {v.contact_available && v.website ? (
            <a href={v.website} target="_blank" rel="noreferrer" className="text-sm text-teal inline-flex items-center gap-1"><Globe className="h-4 w-4" /> Website</a>
          ) : (
            <span className="text-xs text-slate-400 inline-flex items-center gap-1"><Lock className="h-3.5 w-3.5" /> Contact details shown only with vendor consent</span>
          )}
          <Dialog open={claimOpen} onOpenChange={setClaimOpen}>
            <DialogTrigger asChild><Button variant="outline" size="sm" className="ml-auto" data-testid="claim-btn"><Flag className="h-3.5 w-3.5 mr-1" /> Claim this profile</Button></DialogTrigger>
            <DialogContent className="bg-white">
              <DialogHeader><DialogTitle>Claim this business profile</DialogTitle>
                <DialogDescription className="text-xs text-slate-500">Our team will verify your request before transferring profile control.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div><Label>Your name *</Label><Input value={claim.name} onChange={(e) => setClaim({ ...claim, name: e.target.value })} data-testid="claim-name" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Mobile *</Label><Input value={claim.mobile} onChange={(e) => setClaim({ ...claim, mobile: e.target.value })} data-testid="claim-mobile" /></div>
                  <div><Label>Email *</Label><Input value={claim.email} onChange={(e) => setClaim({ ...claim, email: e.target.value })} data-testid="claim-email" /></div>
                </div>
                <div><Label>Message</Label><Textarea rows={2} value={claim.message} onChange={(e) => setClaim({ ...claim, message: e.target.value })} data-testid="claim-message" /></div>
              </div>
              <DialogFooter><Button className="w-full bg-teal-brand text-white" onClick={submitClaim} data-testid="claim-submit">Submit claim request</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      <FeedbackBlock vendorSlug={v.slug} />
      <p className="mt-4 text-xs text-slate-400">This is a demonstration profile. ComplaintAdvisor does not guarantee quotations, appointments, service quality or outcomes.</p>
    </div>
  );
}
